// CHG-0141 (Jessica via Reid 10/30; Connor's record 10/30): the driver's license number had never been sent to
// LexisNexis (0 of 52 runs since 10/1), so it was never checked and the Identity Verification report could not
// say whether it verified. Jessica 11/2: "Name, Address, Date of Birth, Social Security Number, DL Number if we
// are utilizing it to verify a temp ID." The fix (as recorded): pass the ID number and state when the
// customer's ID is a driver's license.
//
// Sources:
//   * Which ID is a driver's license: Core Director Field 254 code 1 = Driver's License (live ParmValSrch
//     IdVerifyCode, AccountOpeningService.IdVerifyAddCoreAsync; individual-config.ts ID_TYPE_CODES '1 · US
//     Driver License'). Field 255 = the ID number, Field 256 = the issuer (state abbreviation for a license).
//   * Where it goes: LexisNexis KB "Fraud Intelligence Rule" request attributes account_drivers_license_number
//     ("Driver license number of state ID number") and account_drivers_license_issuer ("Two-letter state
//     abbreviation of the driver license issuing state") — sent by DdpApiAdapter from SubjectIdentity
//     LicenseNumber / LicenseState.
// Only the PRIMARY ID (Field 254-258) is used: it is the one the officer examined for CIP.

import type { RaoPerson } from '../../../types';

/** Core Director Field 254 code for a driver's license. */
export const DRIVER_LICENSE_ID_TYPE = '1';

function extra(person: RaoPerson, key: string): string {
  const v = (person.extra as Record<string, unknown> | undefined)?.[key];
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * The license number and issuing state to send to LexisNexis, or {} when the person's primary ID is not a
 * driver's license or has no number. The state is sent only when the issuer is a two-letter abbreviation
 * (LexisNexis's format); anything else ("Missouri", "US") is left out rather than guessed.
 */
export function driverLicenseForLn(person: RaoPerson): { licenseNumber?: string; licenseState?: string } {
  if (extra(person, 'idType') !== DRIVER_LICENSE_ID_TYPE) return {};
  const licenseNumber = extra(person, 'idNumber');
  if (!licenseNumber) return {};
  const issuer = extra(person, 'idIssuer').toUpperCase();
  return /^[A-Z]{2}$/.test(issuer) ? { licenseNumber, licenseState: issuer } : { licenseNumber };
}
